import { EyeIcon } from "@heroicons/react/16/solid";
import { useContext, useEffect, useState } from "react";
import { api } from "../../../../api";
import { OverflowFrame } from "../../../../externalComponents";
import { PageHeading } from "../../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../../externalComponents/SettingsSidebar";
import { Button } from "../../../../externalComponents/tailwind/button";
import {
  Field,
  Fieldset,
  Legend,
} from "../../../../externalComponents/tailwind/fieldset";
import {
  Input,
  InputGroup,
} from "../../../../externalComponents/tailwind/input";
import { Text } from "../../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../../providers";
import AlcmeonIntegration from "./AlcmeonIntegration";

export default function CompanyIntegrationPage() {
  const { customer, shop } = useContext(CustomerContext);

  const urlParams = new URLSearchParams(window.location.search);
  const crispWebsiteId = urlParams.get("website_id");

  const [isLoading, setIsLoading] = useState(false);
  const [isConnectKlaviyoLoading, setIsConnectKlaviyoLoading] = useState(false);
  const [isConnectShopifyLoading, setIsConnectShopifyLoading] = useState(false);
  const [shopBillingStatus, setShopBillingStatus] = useState(null);
  const [klaviyoApiKey, setKlaviyoApiKey] = useState("");
  const [gorgiasApiKey, setGorgiasApiKey] = useState("");
  const [splioApiKey, setSplioApiKey] = useState("");
  const [simioApiKey, setSimioApiKey] = useState("");
  const [isSimioApiKeyVisible, setIsSimioApiKeyVisible] = useState(false);

  useEffect(() => {
    if (customer.klaviyoToken) setKlaviyoApiKey(customer.klaviyoToken);
    if (customer.apiKeyGorgias) setGorgiasApiKey(customer.apiKeyGorgias);
    if (customer.apiKeySplio) setSplioApiKey(customer.apiKeySplio);
    if (customer.apiKeySimio) setSimioApiKey(customer.apiKeySimio);
  }, [customer]);

  const save = async () => {
    setIsLoading(true);
    await api.customer.update(customer.id, {
      klaviyoToken: klaviyoApiKey !== "" ? klaviyoApiKey : null,
      apiKeySplio: splioApiKey !== "" ? splioApiKey : null,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (crispWebsiteId && !customer.crispWebsiteId) {
      api.customer.update(customer.id, {
        crispWebsiteId,
      });
      window.location.reload();
    }
  });

  const handleConnectKlaviyo = async () => {
    setIsConnectKlaviyoLoading(true);
    try {
      const installationUrl = await api.generateKlaviyoInstallationUrl({
        customerId: customer.id,
      });
      window.open(installationUrl, "_blank");
    } catch (error) {
      console.error("Error connecting to Klaviyo:", error);
    } finally {
      setIsConnectKlaviyoLoading(false);
    }
  };
  const handleDisconnectKlaviyo = async () => {
    setIsConnectKlaviyoLoading(true);

    try {
      await api.klaviyo.disconnect();
    } catch (e) {
      console.error(e);
    } finally {
      setIsConnectKlaviyoLoading(false);
      window.location.href =
        "/settings/data/integrations/klaviyo/connexion?disconnected=true";
    }
  };

  // Check billing status when shop is available
  useEffect(() => {
    const checkBillingStatus = async () => {
      if (shop?.id) {
        try {
          const response = await fetch(`/api/billing/status?shopId=${shop.id}`);
          if (response.ok) {
            const status = await response.json();
            setShopBillingStatus(status);
          }
        } catch (error) {
          console.error("Error checking billing status:", error);
        }
      }
    };

    checkBillingStatus();
  }, [shop?.id]);

  const handleConnectShopify = async () => {
    // If shop is already connected, enable subscription
    if (shop?.id) {
      setIsConnectShopifyLoading(true);
      try {
        // Call the enableSubscription action on the shopifyShop model
        const result = await api.shopifyShop.enableSubscription(shop.id);

        if (result.error) {
          console.error("Billing error:", result.error);
          alert(`Failed to activate billing: ${result.error.message}`);
          return;
        }

        // Wait 1 second to ensure data is persisted
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Fetch the updated shop record to get confirmationUrl
        const updatedShop = await api.shopifyShop.findOne(shop.id, {
          select: {
            id: true,
            confirmationUrl: true,
          },
        });

        if (!updatedShop) {
          alert("Failed to fetch shop data. Please try again.");
          return;
        }

        const confirmationUrl = updatedShop.confirmationUrl;

        if (!confirmationUrl) {
          console.error("No confirmation URL in shop:", updatedShop);
          alert("No confirmation URL received. Please try again.");
          return;
        }

        // Redirect to Shopify billing confirmation
        window.location.assign(confirmationUrl);
      } catch (error) {
        console.error("Error starting billing:", error);
        const errorMessage =
          error.message || "Failed to activate billing. Please try again.";
        alert(errorMessage);
      } finally {
        setIsConnectShopifyLoading(false);
      }
    } else {
      // No shop connected yet, go to Shopify app store
      window.open("https://apps.shopify.com/simio", "_blank");
    }
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Integrations"
          primaryAction="Save"
          handlePrimaryAction={save}
          isPrimaryLoading={isLoading}
        />
        <OverflowFrame>
          <div className="mt-5">
            {customer && (
              <Fieldset>
                {simioApiKey && (
                  <Field>
                    <Legend>Simio API key</Legend>
                    <div className="flex flex-row">
                      <InputGroup className="flex relative items-center">
                        <Input
                          name="Simio API key"
                          value={simioApiKey}
                          disabled
                          autoComplete="off"
                          type={isSimioApiKeyVisible ? "text" : "password"}
                        />
                      </InputGroup>

                      <Button
                        color="optionButton"
                        className="ml-2"
                        onClick={() =>
                          setIsSimioApiKeyVisible((status) => !status)
                        }
                      >
                        <EyeIcon />
                      </Button>
                    </div>
                  </Field>
                )}
                <Field>
                  <Legend>Shopify</Legend>
                  {shop ? (
                    <>
                      <Text>Your account is linked to {shop.name}'s store</Text>
                      {shopBillingStatus && !shopBillingStatus.planActive && (
                        <Button
                          color="secondaryButton"
                          loading={isConnectShopifyLoading}
                          onClick={handleConnectShopify}
                          className="mt-2"
                        >
                          Activate Billing ({shopBillingStatus.planName})
                        </Button>
                      )}
                      {shopBillingStatus?.planActive && (
                        <Text className="text-green-600 mt-2">
                          ✓ Billing active: {shopBillingStatus.planName}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Button
                      color="secondaryButton"
                      onClick={handleConnectShopify}
                      loading={isConnectShopifyLoading}
                    >
                      Connect Shopify
                    </Button>
                  )}
                </Field>
                <Field>
                  <Legend>Klaviyo</Legend>
                  {customer.klaviyoAccessToken && (
                    <>
                      <Text>Your account is linked to Klaviyo</Text>
                      <Button
                        color="secondaryButton"
                        loading={isConnectKlaviyoLoading}
                        onClick={handleDisconnectKlaviyo}
                      >
                        Disconnect Klaviyo
                      </Button>
                    </>
                  )}
                  {!customer.klaviyoAccessToken && customer.klaviyoToken && (
                    <Input
                      className="mb-2"
                      name="Klaviyo API key"
                      value={klaviyoApiKey}
                      onChange={(e) => setKlaviyoApiKey(e.target.value)}
                      autoComplete="off"
                      maxLength={2000}
                      type={customer.klaviyoToken ? "password" : "text"}
                    />
                  )}
                  {!customer.klaviyoAccessToken && (
                    <Button
                      color="secondaryButton"
                      loading={isConnectKlaviyoLoading}
                      onClick={handleConnectKlaviyo}
                    >
                      Connect Klaviyo
                    </Button>
                  )}
                </Field>

                <Field>
                  <Legend>Splio</Legend>
                  <Input
                    className="mb-2"
                    name="Splio API key"
                    value={splioApiKey}
                    onChange={(e) => setSplioApiKey(e.target.value)}
                    autoComplete="off"
                    maxLength={2000}
                    type={customer.apiKeySplio ? "password" : "text"}
                  />
                </Field>
                <Field>
                  <Legend>Gorgias API key</Legend>
                  {gorgiasApiKey ? (
                    <Input
                      name="Gorgias API key"
                      value={gorgiasApiKey}
                      disabled
                      autoComplete="off"
                      type="password"
                    />
                  ) : (
                    <Text>
                      You want to connect Simio to Gorgias? Ask our team
                    </Text>
                  )}
                </Field>
                <Field>
                  <Legend>Crisp</Legend>
                  {customer?.crispWebsiteId ? (
                    <Text>
                      Your account is linked to Crisp (Website ID:{" "}
                      {customer.crispWebsiteId})
                    </Text>
                  ) : (
                    <Button
                      color="secondaryButton"
                      onClick={() =>
                        window.open(
                          "https://app.crisp.chat/initiate/plugin/5f0419bc-3ab0-4c89-aa54-ceb55df4ce5e/",
                          "_blank",
                        )
                      }
                    >
                      Connect Crisp
                    </Button>
                  )}
                </Field>

                <AlcmeonIntegration customer={customer} />
              </Fieldset>
            )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
